import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { UserDealersService } from "../../api-services/user/user_dealers.service";
import { UserService } from "../../api-services/user/user.service";
import { UserActions } from "./user.actions";
import { catchError, concatMap, delay, of, startWith, switchMap, tap } from "rxjs";
import { Router } from "@angular/router";
import { UserAuthenticateService } from "../../api-services/user/user_authenticate.service";
import { UserPasswordService } from "../../api-services/user/user_password.service";
import { Store } from "@ngrx/store";
import { RootState } from "../store";
import { AppActions } from "../app/app.actions";
import { StoreService } from "../../local-services/store.service";
import { DealerActions } from "../dealer/dealer.actions";
import { UserDealerService } from "../../api-services/user/user_dealer.service";

@Injectable()
export class UserEffects {

  private actions$ = inject(Actions);

  constructor(
    private userService: UserService,
    private userDealersService: UserDealersService,
    private userDealerService: UserDealerService,
    private userAuthenticateService: UserAuthenticateService,
    private userPasswordService: UserPasswordService,
    private router: Router,
    private store: Store<RootState>,
    private storeService: StoreService
  ) { }

  setUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.setUser),
      concatMap(action =>
        this.userAuthenticateService.authenticateUser(action.usernameOrEmail, action.password).pipe(
          switchMap(data => [
            UserActions.setUserSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });
  navigateAfterSetUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.setUserSuccess),
        tap(() => {
          this.router.navigate(['/main'])
        })
      ),
    { dispatch: false }
  );

  resetUserPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.resetUserPassword),
      concatMap((action) =>
        this.userPasswordService.resetUserPassword(action.usernameOrEmail).pipe(
          switchMap(data => [
            UserActions.resetUserPasswordSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        ))
    );
  });
  navigateAfterResetUserPasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.resetUserPasswordSuccess),
      tap(() => this.router.navigate(['/login']))
    ),
    { dispatch: false }
  );

  loadDealers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadUserDealers),
      concatMap(() =>
        this.userDealersService.getDealers().pipe(
          switchMap(data => [
            UserActions.loadUserDealersSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        ))
    );
  });

  changeUserDealer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.changeUserDealer),
      concatMap((action) =>
        this.userDealersService.changeDealer(action.usersDealersId).pipe(
          switchMap(data => [
            UserActions.changeUserDealerSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        ))
    );
  });
  reloadAfterChangeUserDealerSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.changeUserDealerSuccess),
      tap(() => this.storeService.clearStore()),
      delay(0),
      tap(() => window.location.reload())
    ),
    { dispatch: false }
  );

  addSelfToDealer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.addSelfToDealer),
      concatMap((action) =>
        this.userDealerService.addSelfToDealer(action.dealerId).pipe(
          switchMap(() => [
            UserActions.addSelfToDealerSuccess(),
            UserActions.loadUserDealers(),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        ))
    );
  });

  removeSelfFromDealer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.removeSelfFromDealer),
      concatMap((action) =>
        this.userDealerService.removeSelfFromDealer(action.dealerId).pipe(
          switchMap(() => [
            UserActions.removeSelfFromDealerSuccess(),
            UserActions.loadUserDealers(),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        ))
    );
  });

  unsetUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.unsetUser),
      tap(() => {
        this.store.dispatch(AppActions.unsetToolbar());
        this.storeService.clearStore();
        this.router.navigate(['/login']);
      })
    );
  }, { dispatch: false });

  loadUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadUser),
      concatMap(() =>
        this.userService.getUser().pipe(
          switchMap(data => [
            UserActions.loadUserSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  updateUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateUser),
      concatMap((action) =>
        this.userService.updateUser(action.payload).pipe(
          switchMap(data => [
            UserActions.updateUserSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            UserActions.loadUser(),
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  registerUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.registerUser),
      concatMap((action) =>
        this.userService.updateUser(action.payload).pipe(
          switchMap(data => [
            UserActions.registerUserSuccess({ data }),
            DealerActions.acceptDealerInvite({ userId: data.id }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });
  navigateAfterRegisterUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.registerUserSuccess),
        tap(() => {
          this.router.navigate(['/login']);
        })
      ),
    { dispatch: false }
  );


}